import { ApplicationType } from '@domain/configuration/types';
import {
  ChatEditabilityConfig,
  getOrderGuideEditability,
  isOrderGuideDistributorLocked,
  OrderGuideEditability,
  OrderGuideEditabilityState,
} from '@/experiences/sales-portal/domain/order-guides/blocs/utils/editability-utils';
import { useSalesPortalDynamicConfigValue } from '@/experiences/sales-portal/blocs/hooks/use-sales-portal-dynamic-config';

interface UseOrderGuideEditabilityOptions {
  businessOrganizationUUID: string;
  config: ChatEditabilityConfig;
  orderGuide: OrderGuideEditabilityState | null;
  supplierUUID?: string;
}

interface UseOrderGuideEditabilityResult {
  editability: OrderGuideEditability;
  isOrderGuideDistributorLocked: (
    orderGuide: OrderGuideEditabilityState,
  ) => boolean;
  isOrderGuideViewOnly: (orderGuide: OrderGuideEditabilityState) => boolean;
  loading: boolean;
}

/**
 * Editability state for the selected order guide — see
 * {@link getOrderGuideEditability} for each flag's conditions.
 *
 * @param businessOrganizationUUID - Org scoping the dynamic config
 * @param orderGuide - The selected guide's signals; null while none is selected
 * @param config - The chat's editability config from the collections loader
 * @param supplierUUID - Selected account's supplier identifier
 * @returns The selected guide's editability, a per-guide distributor-lock
 *   check, a per-guide view-only check, and loading state
 */
export const useOrderGuideEditability = ({
  businessOrganizationUUID,
  config,
  orderGuide,
  supplierUUID,
}: UseOrderGuideEditabilityOptions): UseOrderGuideEditabilityResult => {
  const { loading, value: applicationType } = useSalesPortalDynamicConfigValue(
    businessOrganizationUUID,
    supplierUUID ?? '',
    'application_type',
    config.chatUUID,
  );

  const isCatalogLimited = applicationType === ApplicationType.CATALOG_ONLY;

  return {
    editability: getOrderGuideEditability({
      customOrderGuideEditingEnabled: config.customOrderGuideEditingEnabled,
      isCatalogLimited,
      orderGuide,
      orderGuideMergeStrategy: config.orderGuideMergeStrategy,
    }),
    isOrderGuideDistributorLocked: (guide) =>
      isOrderGuideDistributorLocked(guide, config.orderGuideMergeStrategy),
    isOrderGuideViewOnly: (guide) =>
      getOrderGuideEditability({
        customOrderGuideEditingEnabled: config.customOrderGuideEditingEnabled,
        isCatalogLimited,
        orderGuide: guide,
        orderGuideMergeStrategy: config.orderGuideMergeStrategy,
      }) !== 'editable',
    loading,
  };
};
